import { filterClasses } from "@swivl-mono/helpers";
import { forwardRef, useId } from "react";
import { IMaskInput } from "react-imask";

import { ErrorText } from "../FormField/FormField.ErrorText";
import { HelperText } from "../FormField/FormField.HelperText";
import { getFormFieldWidthClass } from "../FormField/FormField.Helpers";
import { Label } from "../FormField/FormField.Label";
import { FormFieldWidth } from "../FormField/FormField.Types";
import { InputSize, InputVariant } from "./Input";
import { CURRENCY_OPTIONS } from "./Input.Constants";
import type { InputFieldProps } from "./Input.Field";
import { InputSelectPrefix } from "./Input.SelectPrefix";
import type { SelectPrefixOption } from "./Input.SelectPrefix";

export interface CurrencyInputProps
  extends Omit<InputFieldProps, "error" | "hasPrefix" | "hasSuffix" | "inputSize" | "variant" | "value" | "onChange" | "type"> {
  label?: string;
  helperText?: string;
  error?: string;
  inputSize?: InputSize;
  variant?: InputVariant;
  containerClassName?: string;
  width?: FormFieldWidth;
  required?: boolean;
  currencyOptions?: SelectPrefixOption[];
  selectedCurrency: string;
  onCurrencyChange: (value: string) => void;
  searchable?: boolean;
  value?: string | number;
  onChange?: (value: string) => void;
  allowDecimals?: boolean;
}

export const CurrencyInput = forwardRef<HTMLInputElement, CurrencyInputProps>(
  (
    {
      label,
      helperText,
      error,
      inputSize = InputSize.MEDIUM,
      variant = InputVariant.OUTLINED,
      containerClassName,
      width = FormFieldWidth.FULL,
      required,
      id,
      currencyOptions = CURRENCY_OPTIONS,
      selectedCurrency,
      onCurrencyChange,
      searchable = true,
      value,
      onChange,
      allowDecimals = true,
      ...props
    },
    ref
  ) => {
    const generatedId = useId();
    const inputId = id || generatedId;
    const containerWidthClass = getFormFieldWidthClass(width);
    const resolvedContainerClassName = filterClasses(containerWidthClass, containerClassName);

    const sizeClasses = {
      [InputSize.SMALL]: "h-[26px] px-3 text-sm",
      [InputSize.MEDIUM]: "h-[34px] px-3"
    };

    const InputContent = (
      <InputSelectPrefix
        inputSize={inputSize}
        error={!!error}
        options={currencyOptions}
        selectedValue={selectedCurrency}
        onChange={onCurrencyChange}
        searchable={searchable}
        disabled={props.disabled}
      >
        <div className="relative flex-1">
          <IMaskInput
            id={inputId}
            mask={Number}
            scale={allowDecimals ? 2 : 0}
            thousandsSeparator=","
            radix="."
            mapToRadix={["."]}
            unmask={true}
            value={String(value || "")}
            onAccept={(val: string) => {
              if (onChange) {
                onChange(val);
              }
            }}
            inputRef={ref}
            disabled={props.disabled}
            readOnly={props.readOnly}
            placeholder={props.placeholder}
            className={filterClasses(
              "body-sm w-full rounded-r-sm border transition-all outline-none",
              props.disabled || props.readOnly
                ? "cursor-not-allowed border-grey-400 bg-grey-200 text-grey-500 placeholder:text-grey-500"
                : error
                  ? "border-red-500 bg-white text-grey-900 placeholder:text-grey-600 hover:border-red-500 focus:border-red-500 focus:ring-2 focus:ring-red-100"
                  : "border-grey-400 bg-white text-grey-900 placeholder:text-grey-600 hover:border-orange-500 focus:border-orange-500 focus:ring-2 focus:ring-orange-100",
              sizeClasses[inputSize]
            )}
          />
        </div>
      </InputSelectPrefix>
    );

    return (
      <div className={resolvedContainerClassName}>
        {label && (
          <Label htmlFor={inputId} required={required} className="mb-1">
            {label}
          </Label>
        )}
        {InputContent}
        {error && <ErrorText>{error}</ErrorText>}
        {!error && helperText && <HelperText>{helperText}</HelperText>}
      </div>
    );
  }
);

CurrencyInput.displayName = "CurrencyInput";
